import React, { useState } from 'react';
import './MobileChart.css';
import { TradingViewChart } from '../../../../plugins/charts/TradingViewChart';

interface MobileChartProps {
  selectedInstrument: string;
  currentPrice: string;
  isVisible: boolean; // Hide for digits products
  onInstrumentSelect?: () => void;
  onExpandChart?: () => void;
}

const MobileChart: React.FC<MobileChartProps> = ({
  selectedInstrument,
  currentPrice,
  isVisible,
  onInstrumentSelect,
  onExpandChart
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'1m' | '5m' | '15m' | '1h' | '1d'>('5m');
  const [chartType, setChartType] = useState<'candlestick' | 'line'>('candlestick');

  const timeframes = [
    { value: '1m', label: '1m' },
    { value: '5m', label: '5m' },
    { value: '15m', label: '15m' },
    { value: '1h', label: '1h' },
    { value: '1d', label: '1d' }
  ];

  // Convert instrument ID to display symbol
  const getInstrumentSymbol = (instrumentId: string): string => {
    // Return the instrument ID as-is since we no longer have static mappings
    // In a real implementation, this would fetch the symbol from the instrument service
    return instrumentId || 'No instrument selected';
  };

  const handleTimeframeChange = (timeframe: string) => {
    setSelectedTimeframe(timeframe as '1m' | '5m' | '15m' | '1h' | '1d');
  };

  const handleChartTypeChange = (newChartType: 'candlestick' | 'line') => {
    setChartType(newChartType);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <section className="mobile-chart-section">
      <div className="mobile-chart-container">
        {/* Chart Canvas */}
        <div className="mobile-chart-canvas">
          {!selectedInstrument ? (
            <div style={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#1a1a1a',
              color: '#666',
              border: '1px solid #2a2a2a'
            }}>
              Please select an instrument to view chart
            </div>
          ) : (
            <TradingViewChart
              instrumentId={selectedInstrument}
              timeframe={selectedTimeframe}
              chartType={chartType}
              height={300}
              onTimeframeChange={handleTimeframeChange}
              onChartTypeChange={handleChartTypeChange}
            />
          )}
        </div>

        {/* Chart Header Overlay */}
        <div className="mobile-chart-header">
          <button 
            className="chart-instrument-chip"
            onClick={onInstrumentSelect}
            aria-label={`Select trading instrument, currently ${getInstrumentSymbol(selectedInstrument)}`}
          >
            <span className="instrument-name">{getInstrumentSymbol(selectedInstrument)}</span>
            <span className="instrument-dropdown-icon">▼</span>
          </button>
          
          <div className="chart-price" aria-live="polite">
            {currentPrice}
          </div>
          
          <button 
            className="expand-chart-btn"
            onClick={onExpandChart}
            aria-label="Expand chart to full screen view"
          >
            ⛶
          </button>
        </div>
      </div>

      {/* Timeframe Selector Below Chart */}
      <div className="mobile-timeframe-selector">
        {/* Chart Type Selection */}
        <div className="chart-type-buttons">
          <button
            className={`timeframe-btn chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('line')}
          >
            Line
          </button>
          <button
            className={`timeframe-btn chart-type-btn ${chartType === 'candlestick' ? 'active' : ''}`}
            onClick={() => handleChartTypeChange('candlestick')}
          >
            Candle
          </button>
        </div>
        
        {/* Divider */}
        <div className="selector-divider"></div>
        
        {/* Timeframe Selection */}
        <div className="timeframe-buttons">
          {timeframes.map(timeframe => (
            <button
              key={timeframe.value}
              className={`timeframe-btn ${selectedTimeframe === timeframe.value ? 'active' : ''}`}
              onClick={() => handleTimeframeChange(timeframe.value)}
            >
              {timeframe.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MobileChart;
export { MobileChart };
